import { LitElement, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

export type FleetResizeOrientation = "column" | "row";

/**
 * Draggable split handle for Fleet workspace panels.
 * Dispatches `fleet-resize` with { orientation, index, ratio }.
 */
@customElement("fleet-resize-handle")
export class FleetResizeHandle extends LitElement {
  @property({ type: String }) orientation: FleetResizeOrientation = "column";
  /** Split index: 0 = first/second, 1 = second/third (columns) or top/bottom (rows). */
  @property({ type: Number }) index = 0;
  @property({ type: Number }) ratio = 0.5;
  @property({ type: Number }) minRatio = 0.15;
  @property({ type: Number }) maxRatio = 0.85;

  private dragging = false;
  private startPos = 0;
  private startRatio = 0;

  static styles = css`
    :host {
      flex-shrink: 0;
      position: relative;
      z-index: 6;
      background: transparent;
      transition: background 150ms ease-out;
    }
    :host([orientation="column"]) {
      width: 8px;
      margin-inline: -4px;
      cursor: col-resize;
      min-height: 24px;
    }
    :host([orientation="row"]) {
      height: 8px;
      margin-block: -4px;
      cursor: row-resize;
      min-width: 24px;
    }
    :host::before {
      content: "";
      position: absolute;
      inset: -4px;
    }
    :host(:hover),
    :host(.dragging) {
      background: color-mix(in srgb, var(--accent, #007bff) 40%, transparent);
    }
  `;

  render() {
    return nothing;
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute("orientation", this.orientation);
    this.addEventListener("mousedown", this.onMouseDown);
    this.addEventListener("touchstart", this.onTouchStart, { passive: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("mousedown", this.onMouseDown);
    this.removeEventListener("touchstart", this.onTouchStart);
    this.teardownDrag();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has("orientation")) {
      this.setAttribute("orientation", this.orientation);
    }
  }

  private onMouseDown = (e: MouseEvent) => {
    if (e.button !== 0) {
      return;
    }
    this.beginDrag(e.clientX, e.clientY);
    e.preventDefault();
  };

  private onTouchStart = (e: TouchEvent) => {
    const t = e.touches[0];
    if (!t) {
      return;
    }
    this.beginDrag(t.clientX, t.clientY);
    e.preventDefault();
  };

  private beginDrag(clientX: number, clientY: number) {
    this.dragging = true;
    this.startPos = this.orientation === "column" ? clientX : clientY;
    this.startRatio = this.ratio;
    this.classList.add("dragging");
    document.addEventListener("mousemove", this.onMouseMove);
    document.addEventListener("mouseup", this.onMouseUp);
    document.addEventListener("touchmove", this.onTouchMove, { passive: false });
    document.addEventListener("touchend", this.onTouchEnd);
  }

  private teardownDrag() {
    document.removeEventListener("mousemove", this.onMouseMove);
    document.removeEventListener("mouseup", this.onMouseUp);
    document.removeEventListener("touchmove", this.onTouchMove);
    document.removeEventListener("touchend", this.onTouchEnd);
  }

  private containerEl(): HTMLElement | null {
    const host = this.parentElement;
    if (!host) {
      return null;
    }
    return host.closest(".fleet-workspace, .fleet-unified") as HTMLElement | null;
  }

  private emitRatio(next: number) {
    this.dispatchEvent(
      new CustomEvent("fleet-resize", {
        detail: {
          orientation: this.orientation,
          index: this.index,
          ratio: next,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private onMouseMove = (e: MouseEvent) => {
    if (!this.dragging) {
      return;
    }
    this.updateFromPointer(e.clientX, e.clientY);
  };

  private onTouchMove = (e: TouchEvent) => {
    if (!this.dragging) {
      return;
    }
    const t = e.touches[0];
    if (!t) {
      return;
    }
    this.updateFromPointer(t.clientX, t.clientY);
    e.preventDefault();
  };

  private updateFromPointer(clientX: number, clientY: number) {
    const container = this.containerEl();
    if (!container) {
      return;
    }
    const rect = container.getBoundingClientRect();
    const size = this.orientation === "column" ? rect.width : rect.height;
    const pos = this.orientation === "column" ? clientX : clientY;
    const origin = this.orientation === "column" ? rect.left : rect.top;
    const delta = (pos - this.startPos) / size;
    let next = this.startRatio + delta;
    next = Math.max(this.minRatio, Math.min(this.maxRatio, next));
    this.emitRatio(next);
  }

  private onMouseUp = () => {
    this.dragging = false;
    this.classList.remove("dragging");
    this.teardownDrag();
  };

  private onTouchEnd = () => {
    this.onMouseUp();
  };
}

declare global {
  interface HTMLElementTagNameMap {
    "fleet-resize-handle": FleetResizeHandle;
  }
}
